import { useCallback } from 'react';
import { CandleType, Scale } from '../../../types';
import './index.css';
import { TimeFrame } from '../../../../../types/Bot';

interface Props {
  candleType: CandleType;
  setCandleType: (value: CandleType) => void;
  timeFrames: Array<TimeFrame>;
  selectedTimeFrameIndex: number;
  setSelectedTimeFrameIndex: React.Dispatch<React.SetStateAction<number>>;
  selectedScale: Scale;
  setSelectedScale: React.Dispatch<React.SetStateAction<Scale>>;
  useParentIndicators: boolean;
  setUseParentIndicators: React.Dispatch<React.SetStateAction<boolean>>;
}

const ChartOptions: React.FC<Props> = (props: Props) => {
  const {
    candleType,
    setCandleType,
    timeFrames,
    selectedTimeFrameIndex,
    setSelectedTimeFrameIndex,
    selectedScale,
    setSelectedScale,
    useParentIndicators,
    setUseParentIndicators,
  } = props;

  const isActiveCandleType = useCallback(
    (passedCandleType: CandleType) => candleType === passedCandleType,
    [candleType],
  );

  const isActiveTimeFrame = useCallback(
    (index: number) => index === selectedTimeFrameIndex,
    [selectedTimeFrameIndex],
  );

  const isActiveScale = useCallback(
    (scale: Scale) => scale === selectedScale,
    [selectedScale],
  );

  const getCandleTypeToggleClassName = useCallback(
    (candleType: CandleType) =>
      `chart-options-candle-type ${
        isActiveCandleType(candleType) ? 'option-active' : 'option-non-active'
      }`,
    [isActiveCandleType],
  );

  const getTimeFrameToggleClassName = useCallback(
    (index: number) =>
      `chart-options-candle-type ${
        isActiveTimeFrame(index) ? 'option-active' : 'option-non-active'
      }`,
    [isActiveTimeFrame],
  );

  const getScaleToggleClassName = useCallback(
    (scale: Scale) =>
      `chart-options-candle-type ${
        isActiveScale(scale) ? 'option-active' : 'option-non-active'
      }`,
    [isActiveScale],
  );

  const getUseParentIndicatorToggleClassName = useCallback(
    (useParentIndicators: boolean) =>
      `chart-options-candle-type ${
        useParentIndicators ? 'option-active' : 'option-non-active'
      }`,
    [],
  );

  return (
    <div className="chart-options-container">
      <div className="chart-options-left">
        <div className="chart-options-candle-types">
          <div
            className={getCandleTypeToggleClassName(CandleType.Normal)}
            onClick={() => setCandleType(CandleType.Normal)}
          >
            <h5 className="chart-options-text">N</h5>
          </div>
          <div
            className={getCandleTypeToggleClassName(CandleType.Heikin)}
            onClick={() => setCandleType(CandleType.Heikin)}
          >
            <h5 className="chart-options-text">H</h5>
          </div>
        </div>

        <div className="chart-options-divider" />

        <div className="chart-options-candle-types">
          <div
            className={getScaleToggleClassName(Scale.Linear)}
            onClick={() => setSelectedScale(Scale.Linear)}
          >
            <h5 className="chart-options-text">LIN</h5>
          </div>
          <div
            className={getScaleToggleClassName(Scale.Logarithmic)}
            onClick={() => setSelectedScale(Scale.Logarithmic)}
          >
            <h5 className="chart-options-text">LOG</h5>
          </div>
        </div>

        <div className="chart-options-divider" />

        <div className="chart-options-candle-types">
          <div
            className={getUseParentIndicatorToggleClassName(
              useParentIndicators,
            )}
            onClick={() => setUseParentIndicators((prev) => !prev)}
          >
            <h5 className="chart-options-text">P</h5>
          </div>
        </div>
      </div>

      <div className="chart-options-candle-types">
        {timeFrames.map((timeFrame, index) => (
          <div
            key={timeFrame}
            className={getTimeFrameToggleClassName(index)}
            onClick={() => setSelectedTimeFrameIndex(index)}
          >
            <h5 className="chart-options-text">{timeFrame}</h5>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChartOptions;
